import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "@/lib/utils";

interface ProgressProps
  extends React.ComponentProps<typeof ProgressPrimitive.Root> {
  value?: number;
  color?: string; // Accepts a CSS color value or a Tailwind class
  bgColor?: string; // Accepts a CSS color value or a Tailwind class
  max?: number; // Default is 100
  /** "segmented" renders the tick-meter style (sidebar Org. Usage). */
  variant?: "default" | "segmented";
  /** segmented only: number of ticks. */
  segments?: number;
}

function Progress({
  className,
  value,
  color,
  bgColor,
  max = 100,
  variant = "default",
  segments = 40,
  ...props
}: ProgressProps) {
  if (variant === "segmented") {
    const pct = Math.min(Math.max((value || 0) / (max || 100), 0), 1);
    // Any progress at all shows at least one tick.
    const filled = Math.min(segments, Math.max(pct > 0 ? 1 : 0, Math.round(pct * segments)));
    return (
      <ProgressPrimitive.Root
        data-slot="progress"
        value={Math.min(value || 0, max)}
        max={max}
        className={cn("flex h-2 w-full items-stretch gap-0.5", className)}
        {...props}
      >
        {Array.from({ length: segments }, (_, i) => {
          const isFilled = i < filled;
          const tone = isFilled ? color : bgColor;
          const toneIsClass = tone?.startsWith("bg-");
          return (
            <div
              key={i}
              className={cn(
                "flex-1 rounded-pill transition-colors",
                toneIsClass ? tone : isFilled ? "bg-primary" : "bg-primary/20"
              )}
              style={tone && !toneIsClass ? { backgroundColor: tone } : undefined}
            />
          );
        })}
      </ProgressPrimitive.Root>
    );
  }

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn(
        "relative h-2 w-full overflow-hidden rounded-pill",
        bgColor?.startsWith("bg-") ? bgColor : "bg-primary/20",
        className
      )}
      style={
        bgColor?.startsWith("bg-")
          ? undefined
          : bgColor
          ? { backgroundColor: bgColor }
          : undefined
      }
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className={cn(
          "h-full w-full flex-1 transition-all",
          color?.startsWith("bg-") ? color : "bg-primary"
        )}
        style={{
          transform: `translateX(-${100 - ((value || 0) / (max || 100)) * 100}%)`,
          backgroundColor: color?.startsWith("bg-")
            ? undefined
            : color || undefined,
        }}
      />
    </ProgressPrimitive.Root>
  );
}

export { Progress };
